import { _ } from "libs";

import React, { useRef, useEffect, useLayoutEffect } from "react";
import ConversationHeader from "./conversationHeader";
import MessageEditor from "./messageEditor";
import TypingIndicator from "./typingIndicator";
import { ChatConversation, Profile } from "../../types";
import Messages from "./messages";
import withRedux from "../../withRedux";
import { ChatState } from "../../reducers/chat";
import { sendMessage, sendTyping, sendTypingStop, markConversationRead } from "../../actions/chat";
import { getDefaultMessage } from "./defaultMessages";
import { filterPartners } from "./functions";

type Props = {
	activeConversationId: string;
	userId: string;
	conversation: ChatConversation;
	sendTyping: (conversationId: string, userId: string) => void;
	sendTypingStop: (conversationId: string, userId: string) => void;
	sendMessage: (message: string, conversationId: string) => void;
	markConversationRead: typeof markConversationRead;
	defaultMessageType?: string;
	messageParams?: object;
	profile: Profile;
};

const ConversationDetail = ({
	activeConversationId,
	userId,
	conversation,
	sendMessage,
	sendTyping,
	sendTypingStop,
	markConversationRead,
	defaultMessageType = "",
	messageParams = {},
	profile,
}: Props) => {
	const editor = useRef<typeof MessageEditor>(null as typeof MessageEditor);

	const body = useRef<HTMLDivElement>(null as HTMLDivElement);

	useLayoutEffect(() => {
		if (body.current) {
			body.current.scrollTop = body.current.scrollHeight;
			body.current.classList.add("ready");
		}

		let timerId = null;
		if (conversation) {
			timerId = setTimeout(() => {
				markConversationRead(conversation.conversationId);
			}, 1000);
		}

		return () => clearTimeout(timerId);
	}, [activeConversationId]);

	useEffect(() => {
		if (editor.current) {
			//@ts-ignore
			editor.current.focus();
		}
	}, [conversation]);

	if (!conversation) {
		return null;
	}

	const handleSendMessage = (message: string) => {
		if (message.length === 0) {
			return;
		}
		sendMessage(message, conversation.conversationId);

		if (defaultMessageType) {
			window.history.pushState({}, document.title, window.location.pathname);
			//@ts-ignore
			editor.current.clear();
		}

		setTimeout(function() {
			body.current.scrollTop = body.current.scrollHeight;
		}, 500);
	};

	const handleOnTyping = () => {
		sendTyping(conversation.conversationId, userId);
	};

	const handleOnTypingStop = () => {
		sendTypingStop(conversation.conversationId, userId);
	};

	const partnersWithoutUser = filterPartners(conversation.partners, userId);

	let defaultMessage = "";

	if (defaultMessageType && window.location.search.includes("type")) {
		defaultMessage = getDefaultMessage(defaultMessageType, {
			firstNameChallengee: conversation.partners.filter(partner => partner.userId !== profile.userId)[0]
				.firstName,
			firstNameChallenger: profile.firstName,
			...messageParams,
		});
	}

	return (
		<div className="conversation" data-is-group-conversation={partnersWithoutUser.length > 1}>
			<ConversationHeader partners={partnersWithoutUser} />
			<div className="conversationBody fadeInMessages" ref={body}>
				<Messages conversation={conversation} userId={userId} />
				<TypingIndicator partners={conversation.partnersHash} myId={userId} />
			</div>
			<div className="conversationFooter">
				<MessageEditor
					onSend={handleSendMessage}
					onTyping={handleOnTyping}
					onTypingStop={handleOnTypingStop}
					conversationId={conversation.conversationId}
					ref={editor}
					defaultMessage={defaultMessage}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = ({ chat: { activeConversation, activeConversationId } }: { chat: ChatState }) => {
	return { conversation: activeConversation, activeConversationId };
};

export default withRedux(
	{
		sendMessage,
		sendTyping,
		sendTypingStop,
		markConversationRead,
	},
	mapStateToProps
)(ConversationDetail);
